import {
  Stack,
  Text,
  Textarea,
  Group,
  Button,
  Table,
  ActionIcon,
  Avatar,
} from '@mantine/core';
import { BiEdit } from 'react-icons/bi';
import { HiLocationMarker } from '@react-icons/all-files/hi/HiLocationMarker';
import { OfferShiftFormProps } from '@company/types/worker-modal.type';
import { getFormattedTime } from '@services/shift/shiftService';
import { capitalize } from 'lodash';
import { convertToTimezone, UTC_TIMEZONE } from '@utils/DateUtils';

export default function OfferShiftForm({
  selectedShifts,
  worker,
  offerNote,
  onOfferNoteChange,
  onSubmit,
  onCancel,
  isLoading,
  onEditShift,
  jobTimezone,
}: OfferShiftFormProps) {
  return (
    <Stack gap="md">
      {/* Worker Selection */}
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          To
        </Text>
        <Group
          gap="md"
          p="sm"
          bd="1px solid var(--anti-flash-white)"
          bdrs="4px"
          bg="var(--light-gray)"
        >
          <Avatar size="sm" src={worker.profileImageUrl} />
          <Text size="sm" fw={500}>
            {capitalize(worker.firstName)} {capitalize(worker.lastName)}
          </Text>
          <Group gap="xs" align="center">
            <HiLocationMarker size={16} />
            <Text size="sm" c="dimmed">
              {worker.address?.city}, {worker.address?.state?.ansiAbbreviation}
            </Text>
          </Group>
        </Group>
      </Stack>

      {/* Shift Details Table */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Position</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Scheduled Time</Table.Th>
            <Table.Th ta="center">Full/Back Up</Table.Th>
            <Table.Th ta="center">Wage</Table.Th>
            <Table.Th ta="center">Paid Break</Table.Th>
            <Table.Th style={{ width: '40px' }}>Edit</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {selectedShifts.map((shift, index) => (
            <Table.Tr key={`${shift.id}-${shift.positionIndex || index}`}>
              <Table.Td>
                <Text size="sm">{shift.title.name}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">
                  {convertToTimezone(
                    new Date(shift.start),
                    jobTimezone || UTC_TIMEZONE
                  ).toLocaleDateString()}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">
                  {getFormattedTime(shift, jobTimezone || 'UTC')}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ta="center">
                  {capitalize(shift.type)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ta="center">
                  ${shift.wage.payRate}/{capitalize(shift.wage.payRatePeriod)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ta="center">
                  {shift.breakPaid ? 'Yes' : 'No'}
                </Text>
              </Table.Td>
              <Table.Td>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  c="black"
                  onClick={() => onEditShift?.(shift)}
                  style={{ cursor: 'pointer' }}
                >
                  <BiEdit size={20} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {/* Offer Note */}
      <Textarea
        label="Offer note (optional)"
        value={offerNote}
        onChange={(event) => onOfferNoteChange(event.currentTarget.value)}
      />

      {/* Action Buttons */}
      <Group justify="flex-end" gap="sm">
        <Button variant="light" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button color="yellow" onClick={onSubmit} loading={isLoading}>
          Offer Shift
        </Button>
      </Group>
    </Stack>
  );
}
